import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useDiagnostic from '../hooks/useDiagnostic';
import RegistrationForm from '../components/diagnostic/RegistrationForm';
import AssessmentQuiz from '../components/diagnostic/AssessmentQuiz';
import ResultsDisplay from '../components/diagnostic/ResultsDisplay';
import { PageHead, Section, Eyebrow, PageTitle, Lede, SectionHead, Button, Card, CardTitle, Grid, Tag, Saying } from '../components/ui';
import Ladder from '../components/Ladder';

/**
 * DiagnosticPage — Redesign V5 (June 2026)
 * Main orchestrator for the AI Fluency Compass.
 * Manages state flow: Landing -> Registration -> Assessment -> Results.
 *
 * LOGIC UNCHANGED from pre-redesign version: useDiagnostic hook, stage
 * machine, error handling, and child component contracts are identical.
 * Only the landing-stage UI is restyled to the navy/emerald token system.
 * V5 sweep: "The 5 Capabilities" -> the 5Cs; em-dashes removed.
 */
const DiagnosticPage = () => {
  const [stage, setStage] = useState('landing'); // landing, registration, assessment, results
  const [assessmentData, setAssessmentData] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const { startAssessment, submitAssessment, loading } = useDiagnostic();

  // Check for saved progress on mount
  useEffect(() => {
    const savedProgress = localStorage.getItem('compass_progress');
    if (savedProgress && stage === 'landing') {
      try {
        // Optionally, you can auto-resume here
        // For now, we'll just let them restart
      } catch (err) {
        console.error('Failed to parse saved progress:', err);
      }
    }
  }, [stage]);

  // Handle starting the assessment
  const handleStartAssessment = async (userData) => {
    setError(null);
    try {
      const data = await startAssessment(userData);
      setAssessmentData(data);
      setStage('assessment');
    } catch (err) {
      console.error('Start assessment error:', err);
      setError(err.message || 'Failed to start assessment. Please try again.');
    }
  };

  // Handle submitting the assessment
  const handleSubmitAssessment = async (submissionData) => {
    setError(null);
    try {
      const resultData = await submitAssessment(submissionData);
      setResults(resultData);
      setStage('results');
    } catch (err) {
      setError(err.message || 'Failed to submit assessment. Please try again.');
      console.error('Submit assessment error:', err);
    }
  };

  // Handle "Get Started" from landing page
  const handleGetStarted = () => {
    setStage('registration');
  };

  // Render based on current stage
  if (stage === 'results' && results) {
    return <ResultsDisplay results={results} />;
  }

  if (stage === 'assessment' && assessmentData) {
    return (
      <AssessmentQuiz
        questions={assessmentData.questions}
        resultId={assessmentData.result_id}
        onComplete={handleSubmitAssessment}
      />
    );
  }

  if (stage === 'registration') {
    return <RegistrationForm onStart={handleStartAssessment} />;
  }

  // Landing page (default)
  return (
    <div className="min-h-screen bg-navy-50 dark:bg-navy-900">
      <PageHead
        title="The CoThink Compass"
        description="Free, ten questions, ten minutes. The CoThink Compass places you on the AI Fluency Ladder across the 5Cs and sends a report with a development path to your inbox."
        path="/compass"
      />

      <Section tone="page" reveal={false}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <Eyebrow>Free instrument</Eyebrow>
            <PageTitle>The CoThink <span className="text-emerald-600 dark:text-emerald-300">Compass</span></PageTitle>
            <Lede>Find out where you stand on the AI Fluency Ladder, across the 5Cs, and what to build next. Ten questions, two per capability, drawn from a bank of fifty. A report with a development path lands in your inbox.</Lede>
            <div className="mt-5 flex flex-wrap gap-2">
              <Tag>Free</Tag><Tag>No account</Tag><Tag>Ten questions</Tag><Tag>Ten minutes</Tag><Tag>Report by email</Tag>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={handleGetStarted} disabled={loading}>{loading ? 'Starting' : 'Take the Compass'}</Button>
              <Button href="#ladder" variant="secondary">The AI Fluency Ladder</Button>
            </div>
          </div>
          <div className="md:col-span-4">
            <Card accent>
              <CardTitle>What you get</CardTitle>
              <ul className="mt-3 flex flex-col gap-2">
                {[
                  'Your stage on the AI Fluency Ladder',
                  'A breakdown across the 5Cs',
                  'Specific next steps, by capability',
                  'A report you can return to in 90 days',
                ].map((item) => (
                  <li key={item} className="font-sans text-interface text-navy-600 dark:text-[#C9D2E0] pl-5 relative before:content-[''] before:absolute before:left-0 before:top-[9px] before:h-2 before:w-2 before:rounded-full before:bg-emerald-500">{item}</li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </Section>

      <Section tone="card">
        <Eyebrow>What it measures</Eyebrow>
        <SectionHead>The 5Cs.</SectionHead>
        <p className="prose-rbl mt-4">Each question maps to one of the 5Cs: Conceptual Clarity, Creative Exploration, Critical Evaluation, Constructive Collaboration, and Cognitive Agility. They are the five human capabilities that define AI Fluency, and they are laid out in full under <Link to="/why-it-matters#ai-fluency" className="text-emerald-600 dark:text-emerald-300 font-sans font-medium">why it matters</Link>.</p>
      </Section>

      <Section tone="page" id="ladder" className="scroll-mt-20">
        <Eyebrow>The AI Fluency Ladder</Eyebrow>
        <SectionHead>Where the Compass places you.</SectionHead>
        <p className="prose-rbl mt-4">Your score puts you on one of five stages. Each has a characteristic pattern and a specific risk that can stall progress if it goes unnamed. Where you are shapes how you use AI, what risks you carry, and what to build next.</p>
        <div className="mt-8"><Ladder /></div>
        <Saying className="mt-12">Where you are on this ladder shapes everything: how you use AI, what risks you carry, and what to build next.</Saying>
      </Section>

      <Section tone="card">
        <Grid cols={3}>
          {[
            { stat: '150+', label: 'Compass completions in year one' },
            { stat: '5', label: 'Capabilities measured: the 5Cs' },
            { stat: '10 min', label: 'Typical time to complete' },
          ].map((s) => (
            <div key={s.label} className="rounded-lg border border-navy-200 dark:border-navy-700 bg-white dark:bg-navy-850 p-5">
              <div className="font-sans font-semibold text-stat-lg text-navy-800 dark:text-white">{s.stat}</div>
              <div className="font-sans text-interface mt-2 text-navy-600 dark:text-[#C9D2E0]">{s.label}</div>
            </div>
          ))}
        </Grid>
      </Section>

      <Section tone="card" reveal={false}>
        <div className="max-w-[92ch]">
          <Eyebrow>Ten minutes</Eyebrow>
          <SectionHead>Find your stage. Then build from it.</SectionHead>
          <p className="font-display text-lede mt-4 text-navy-600 dark:text-[#C9D2E0]">Take the Compass and get a report across the 5Cs, with next steps, in your inbox.</p>
          <div className="mt-8"><Button onClick={handleGetStarted} disabled={loading}>{loading ? 'Starting' : 'Take the Compass'}</Button></div>
        </div>
      </Section>

      {error && (
        <div role="alert" className="fixed bottom-4 right-4 bg-state-priority text-white px-6 py-4 rounded-lg shadow-lg max-w-md z-50">
          <p className="font-sans font-medium">{error}</p>
          <button
            onClick={() => setError(null)}
            className="absolute top-2 right-2 text-white h-8 w-8"
            aria-label="Dismiss error"
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

export default DiagnosticPage;
